/**
 * MercadoPago preapproval (suscripción AR) — gestión canónica del
 * `preapproval_plan_id` que alimenta el hero CTA "Ser Exclusivo" de AR
 * (BaseTemplate → resuelto por el Worker desde cta-por-region / cascada env).
 *
 * El plan_id viaja EMBEBIDO en la URL del checkout hosted de MP
 * (`/subscriptions/checkout?preapproval_plan_id=<id>`). El operador puede
 * pegar el id pelado O la URL completa; acá normalizamos a la URL canónica,
 * extraemos el id, y validamos que sea un checkout de MercadoPago real.
 *
 * Guard de merchant (anti cross-brand): un preapproval_plan_id pertenece a UN
 * merchant MP. Si el operador pega el plan de otro merchant (o queda un valor
 * stale), los AR de esta brand se suscriben en la cuenta equivocada y la plata
 * se va al bolsillo equivocado (bug observado 2026-06-07). Este módulo no puede
 * verificar la propiedad del plan sin llamar a la API de MP, pero SÍ valida la
 * forma (host mercadopago.com.ar + plan_id con formato de id MP) y expone el id
 * para que el admin lo confirme visualmente contra sus planes gestionados.
 */

const MP_CHECKOUT_HOST = "www.mercadopago.com.ar";
const MP_CHECKOUT_PATH = "/subscriptions/checkout";

/** Un preapproval_plan_id de MP es un hex de 32 (a veces con guiones tipo UUID). */
const MP_PLAN_ID_RE = /^[a-f0-9]{16,64}$|^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

/** ¿La URL apunta a un checkout de suscripción de MercadoPago? */
export function isMercadoPagoCheckoutUrl(url: string | undefined | null): boolean {
  const u = (url ?? "").trim();
  if (!u) return false;
  return /mercadopago\.com/i.test(u) || /preapproval_plan_id=/i.test(u);
}

/** Extrae el preapproval_plan_id de una URL de MP (o de un id pelado). */
export function extractMpPreapprovalPlanId(input: string | undefined | null): string | undefined {
  const u = (input ?? "").trim();
  if (!u) return undefined;
  // id pelado (sin esquema ni query)
  if (!/[/:?=]/.test(u) && MP_PLAN_ID_RE.test(u)) return u;
  try {
    const parsed = new URL(u, `https://${MP_CHECKOUT_HOST}`);
    const id = parsed.searchParams.get("preapproval_plan_id");
    return id?.trim() || undefined;
  } catch {
    const match = u.match(/preapproval_plan_id=([a-f0-9-]+)/i);
    return match?.[1];
  }
}

export type KnownArMpPlan = {
  planId: string;
  /** Nombre del plan (reason en MP). */
  reason: string;
  /** Tier en la DB (mercadopago_plans.plan_tier). */
  tier: string;
  /** Monto mensual en ARS. */
  amountArs: number;
};

/**
 * Planes MP reales de la cuenta (AR) verificados ACTIVOS en la API de MP.
 * Referencia para el picker de cta-por-region (labels + fallback si el fetch
 * en vivo a /api/admin/mercadopago-plans falla). El source de verdad sigue
 * siendo ese endpoint (token en Railway, server-side); esto solo evita una
 * pantalla vacía y da nombres legibles. Los planes en 404 (mejores_amigos,
 * vip_discount, vip) NO se listan acá: sus links están muertos y elegirlos
 * daría un checkout roto — recrearlos en MercadoPago Plans antes de usarlos.
 */
export const KNOWN_AR_MP_PLANS: readonly KnownArMpPlan[] = [
  { planId: "e19b33ec241e4a49aa24b21eaacea062", reason: "Membresía Básica", tier: "basic", amountArs: 14999 },
  { planId: "e106ffecaf3c4f5998d78f15d4e3420d", reason: "Canal WhatsApp Delfina", tier: "basic", amountArs: 6999 },
  // Comunidad / invitacion-premium VIP AR (canónico 2026-07): $49.999/mes
  {
    planId: "4d4a2fa02a964825808f860dcdd812a4",
    reason: "Membresía Exclusivo AR 49999",
    tier: "vip",
    amountArs: 49999,
  },
  // Legacy VIP AR $19.999 — no usar en comunidad/invitacion-premium
  { planId: "e7c9dc600bd542e29db1425a1ee3eb6c", reason: "Membresía Exclusivo AR (legacy 19999)", tier: "vip_legacy", amountArs: 19999 },
  { planId: "1fc5a1c24c074c069edd0378531e9fad", reason: "Miembro Exclusivo", tier: "api", amountArs: 9999 },
];

/** Plan VIP AR canónico para Comunidad + `/invitacion-premium` (MP preapproval). */
export const CANONICAL_AR_VIP_MP_PLAN_ID = "4d4a2fa02a964825808f860dcdd812a4";
/** URL canónica (no usar build* acá: se exporta antes de la función en el módulo). */
export const CANONICAL_AR_VIP_MP_CHECKOUT_URL =
  `https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=${CANONICAL_AR_VIP_MP_PLAN_ID}`;

/**
 * preapproval_plan_id que NO son del merchant MP de esta brand — verificados en
 * vivo (2026-07-12) renderizando el checkout de OTRA creadora (Antonela Diaz).
 * La DB/config se clonó de antoneladiaz, así que estos valores reaparecen si
 * alguien recopia envs. Un AR que entre por acá le paga a la cuenta equivocada
 * (mismo bug 2026-06-07). `normalizeMercadoPagoCheckoutInput` los rechaza para
 * que ningún getter/admin los use aunque el env quede contaminado.
 */
export const KNOWN_FOREIGN_MP_PLAN_IDS: ReadonlySet<string> = new Set([
  "eb2bad2f7ab34fb29f21df1d7421b3bd",
  "abd6d2596cdb4932a9dc9e6c284d08a2",
]);

/** ¿El preapproval_plan_id pertenece a un merchant ajeno confirmado? */
export function isForeignMpPlanId(planId: string | undefined | null): boolean {
  const id = (planId ?? "").trim().toLowerCase();
  return id !== "" && KNOWN_FOREIGN_MP_PLAN_IDS.has(id);
}

/** Metadata de un plan conocido por su preapproval_plan_id (para badges/labels). */
export function findKnownArMpPlan(planId: string | undefined | null): KnownArMpPlan | undefined {
  const id = (planId ?? "").trim().toLowerCase();
  if (!id) return undefined;
  return KNOWN_AR_MP_PLANS.find((p) => p.planId.toLowerCase() === id);
}

/** URL canónica del checkout hosted de MP para un preapproval_plan_id. */
export function buildMercadoPagoPreapprovalCheckoutUrl(planId: string | undefined | null): string {
  const id = (planId ?? "").trim();
  if (!id) return "";
  return `https://${MP_CHECKOUT_HOST}${MP_CHECKOUT_PATH}?preapproval_plan_id=${encodeURIComponent(id)}`;
}

export type MpCheckoutNormalizeResult = {
  /** true si se pudo derivar un checkout MP válido a partir del input. */
  valid: boolean;
  /** preapproval_plan_id detectado (o undefined si no se encontró). */
  planId?: string;
  /** URL canónica reconstruida desde el planId (o "" si inválido). */
  url: string;
  /** Motivo legible cuando valid=false (para mostrar inline en el admin). */
  reason?: string;
};

/**
 * Normaliza lo que pegó el operador (plan_id pelado O URL completa) a una URL
 * canónica de checkout MP. Fuente única para el admin (cta-por-region) y para
 * el guard de la cascada env. No hace fetch — validación de forma únicamente.
 */
export function normalizeMercadoPagoCheckoutInput(
  input: string | undefined | null,
): MpCheckoutNormalizeResult {
  const raw = (input ?? "").trim();
  if (!raw) return { valid: false, url: "", reason: "Vacío" };

  const planId = extractMpPreapprovalPlanId(raw);
  if (!planId) {
    // Es una URL pero sin preapproval_plan_id → no sirve para suscripción.
    if (/^https?:\/\//i.test(raw)) {
      return {
        valid: false,
        url: "",
        reason: isMercadoPagoCheckoutUrl(raw)
          ? "URL de MercadoPago sin preapproval_plan_id — la membresía es recurrente y lo necesita"
          : "No es una URL de checkout de MercadoPago (falta preapproval_plan_id)",
      };
    }
    return {
      valid: false,
      url: "",
      reason: "No parece un preapproval_plan_id ni una URL de checkout de MercadoPago",
    };
  }

  if (!MP_PLAN_ID_RE.test(planId)) {
    return {
      valid: false,
      planId,
      url: "",
      reason: `preapproval_plan_id con formato inesperado: "${planId}"`,
    };
  }

  if (isForeignMpPlanId(planId)) {
    return {
      valid: false,
      planId,
      url: "",
      reason:
        "Ese preapproval_plan_id es de OTRO merchant de MercadoPago (no es de esta marca) — los pagos irían a la cuenta equivocada",
    };
  }

  return { valid: true, planId, url: buildMercadoPagoPreapprovalCheckoutUrl(planId) };
}
